// Code generated by contracts/generate.ts. DO NOT EDIT.
//
// Edit the JSON under contracts/data/ and run `pnpm contracts:generate`.
// CI fails if this file is hand-edited or left stale.

import { TrackAudioVisualSettings } from './song-settings';

/**
 * What a file in the bundle is for. The bare track names are the mid range;
 * `_low`/`_high` are the alternate ranges of the same track. `full_mix` is
 * the original recording, `auslan` a sign-language video.
 */
export type InnerFilePurpose =
  | 'full_mix'
  | 'vocals'
  | 'drums'
  | 'bass'
  | 'melody'
  | 'haptic-drums'
  | 'haptic-bass'
  | 'vocals_low'
  | 'vocals_high'
  | 'bass_low'
  | 'bass_high'
  | 'melody_low'
  | 'melody_high'
  | 'auslan';

/** Every InnerFilePurpose value, in declaration order. */
export const ALL_INNER_FILE_PURPOSES: readonly InnerFilePurpose[] = Object.freeze([
  'full_mix',
  'vocals',
  'drums',
  'bass',
  'melody',
  'haptic-drums',
  'haptic-bass',
  'vocals_low',
  'vocals_high',
  'bass_low',
  'bass_high',
  'melody_low',
  'melody_high',
  'auslan',
]);

/** What the song is, as opposed to how it is put together. */
export interface SongMetadata {
  title: string;
  artist: string;
  /**
   * Contentful genre ids, or legacy genre names for older songs. Resolve
   * through @amplio/genres rather than comparing strings.
   */
  genre: string[];
  yearReleased?: number;
  mood?: string;
  meaning?: string;
  history?: string;
  credits?: string;
  licenses?: string[];
}

/** How this particular bundle was produced. */
export interface AmplioFileMetadata {
  /**
   * Version of this bundle format, currently "0.2". Nothing branches on it
   * yet, which is precisely why it must keep being written.
   */
  fileVersion: string;
  /**
   * The song's own revision, bumped by the tool on re-export. A decimal
   * string, not a number.
   */
  songVersion: string;
  datePublished?: string;
  ISRC?: string;
  /** Song length in seconds. */
  duration?: number;
  /**
   * Which generation of the audio renderer produced these stems. Absent
   * means 1: stems at whatever level the soundfont preset happened to
   * produce. 2 means they are loudness-matched to each other, which is what
   * selects between TRACK_DEFAULTS and LEGACY_TRACK_DEFAULTS.
   */
  audioRenderVersion?: number;
  /**
   * Read by the player; the tool does not write it today, so treat it as
   * absent rather than as a value to rely on.
   */
  bpm?: number;
  /** Read by the player; the tool does not write it today. */
  key?: string;
  /** Read by the player; the tool does not write it today. */
  timeSignature?: string;
  /**
   * What the tool last published from this file. Absent on files written
   * before the tool started recording it, and on files that have never been
   * published.
   */
  publishBase?: PublishBase;
}

/**
 * A snapshot of the song metadata and track defaults as they were at the
 * last publish, so a later publish can tell an edit made here from an edit
 * made on the website and only ask about the fields both sides changed.
 */
export interface PublishBase {
  /** The metadata exactly as it went to song-inbox/. */
  song: SongMetadata;
  /** ISO 8601, written by the tool at publish time. */
  publishedAt: string;
  /** The song version that carried this publish, for traceability. */
  songVersion: string;
  /**
   * The track defaults exactly as they went to song-inbox/. Absent on bases
   * written before track defaults were reconciled; such a base is read as
   * matching the published defaults, so the first publish after this shipped
   * asks nothing.
   */
  defaults?: TrackAudioVisualSettings;
}

/** One media file inside the bundle. */
export interface AmplioInnerFile {
  /** Path relative to the zip root. */
  path: string;
  type: 'midi' | 'audio' | 'video';
  purpose: InnerFilePurpose;
  /**
   * The user attached this file themselves; it is not part of the published
   * song.
   */
  isUserAdded?: boolean;
  /**
   * Audio produced by rendering the bundle's MIDI rather than recorded, so
   * it can be regenerated and need not be shipped.
   */
  isMidiRenderedAudio?: boolean;
  /** Which melody this file is, when a song offers more than one. */
  melodyIndex?: number;
}

export interface AmplioLyricSegment {
  /** Seconds from the start of the song. */
  startTime: number;
  endTime: number;
  text: string;
}

/**
 * The JSON manifest inside a `.amplio` bundle.
 *
 * The tool is the only writer and the player is the only reader, across a
 * process, a machine and a language boundary, with no version negotiation in
 * between. A field one side writes and the other does not read is silently
 * lost data; a field the reader requires and the writer stops emitting is a
 * file that will not open. That makes this the highest-value contract here
 * and the one most worth reading before changing.
 *
 * The reader is deliberately permissive: unknown keys are ignored and
 * `type`/`purpose` stay strings on the Kotlin side, so a bundle written by a
 * newer tool still opens in an older player instead of failing to parse
 * outright. TypeScript keeps them as unions because there the vocabulary is
 * checked at build time and being wrong is free to fix.
 */
export interface AmplioFileRaw {
  /**
   * Stable identifier for the song, generated by the tool when the file is
   * first created and preserved across re-exports. Bundles predating it
   * exist, so the player must tolerate its absence.
   */
  songId: string;
  song: SongMetadata;
  /** Every media file in the bundle, by path relative to the zip root. */
  files: AmplioInnerFile[];
  metadata: AmplioFileMetadata;
  /**
   * Display name per MIDI track id, when the authored name should not be
   * shown. Written by the tool; not read by the player yet.
   */
  trackLabels?: Record<string, string>;
  /**
   * Melody track indices in the order they should be offered. Written by the
   * tool; not read by the player yet.
   */
  melodyPriority?: number[];
  /**
   * Settings the song was authored with. The starting point for a user who
   * has never touched the controls.
   */
  defaults?: TrackAudioVisualSettings;
  /**
   * The user's own saved overrides, merged over `defaults`. Read by the
   * player; the tool does not currently write it into the bundle.
   */
  preferredDefaults?: TrackAudioVisualSettings;
  /**
   * Timed lyrics. Read by the player as a fallback when the MIDI carries no
   * lyric events; the tool emits lyrics through the MIDI instead, so this is
   * usually absent.
   */
  lyrics?: AmplioLyricSegment[];
}
